import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { UiPrefsPatch } from '@arcanum-hq/shared-types';

/**
 * Persists per-conversation UI preferences via
 * `PATCH /api/conversations/:id/ui-prefs`.
 *
 * model-view-ux-polish Phase 3 Session 3.4 (2026-04-27).
 *
 * The local `<ModelLayout>` state is the source of truth for the dragging
 * tab; this mutation is a fire-and-forget persistence sink.
 *
 * **No `onSuccess` cache invalidation by design** — spec §7 explicitly
 * says "no live mirroring of the other tab's drag — column widths are a
 * personal preference, not collaborative state." Invalidating
 * `queryKeys.conversations.detail()` would refetch and snap the optimistic
 * local state mid-debounce. Cross-tab visibility is on next page load,
 * which is sufficient.
 *
 * **No retry** — TanStack default `retry: 3` would amplify a flapping API
 * across the 800ms debounce window. If the server is unreachable the user
 * loses their widths on next reload, which is a recoverable degradation.
 */
export function useUiPrefs(conversationId: string | null) {
  return useMutation({
    mutationFn: async (patch: UiPrefsPatch) => {
      if (!conversationId) return;
      // PATCH returns 204 with no body; api.patch wraps fetch and returns
      // an empty object for 204 (see lib/api.ts:177).
      await api.patch(`/api/conversations/${conversationId}/ui-prefs`, patch);
    },
    retry: 0,
  });
}
